import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PersistentStateManager implements OnDestroy {
  private readonly persistentStates = new Map<
    string,
    BehaviorSubject<unknown>
  >();
  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.persistentStates.forEach(state => {
      state.complete();
    });
    this.persistentStates.clear();
  }

  setState<T>(key: string, state: T): void {
    if (!key) {
      console.warn('Cannot set state: key is required');
      return;
    }

    if (this.persistentStates.has(key)) {
      this.persistentStates.get(key)?.next(state);
    } else {
      this.persistentStates.set(key, new BehaviorSubject<unknown>(state));
    }
  }

  getState<T>(key: string): Observable<T> {
    if (!key) {
      console.warn('Cannot get state: key is required');
      return new BehaviorSubject<T>(null as unknown as T).asObservable();
    }

    if (!this.persistentStates.has(key)) {
      this.persistentStates.set(key, new BehaviorSubject<unknown>(null));
    }
    return this.persistentStates.get(key)!.asObservable() as Observable<T>;
  }

  clearState(key: string): void {
    if (!key) return;

    const state = this.persistentStates.get(key);
    if (state) {
      state.next(null);
    }
  }

  hasState(key: string): boolean {
    return this.persistentStates.has(key);
  }

  getAllStateKeys(): string[] {
    return Array.from(this.persistentStates.keys());
  }
}
